import React, { FC, useCallback, useEffect } from "react"
import { Title, Text, OnboardingScreenContainer, Button } from "dash-styled"
import { OnboardingProgress } from "../../../../Common/OnboardingProgress/OnboardingProgress"
import { TemplateInstantiateStage } from "../../../../../../../Models/Client/TemplateInstantiation"
import { ReactComponent as BlankProjectIcon } from "icons/lib/flask.svg"
import { ReactComponent as SampleProjectIcon } from "icons/lib/sample.svg"
import { SampleAppMetadata } from "../../../../../../../Models/DataProgram/TemplateMetadata"
import { useDispatch } from "react-redux"
import { clearInputsForId } from "../../../../../../../Actions/Client/InputRender/InputRenderActions"

interface SelectProjectStageProps {
    onBack: () => void
    onForward: () => void
    setSampleAppIdentifier: (sampleAppIdentifier?: string) => void
    sampleApps: SampleAppMetadata[]
    paramsFormId: string
    inputFormId: string
}

export const SelectProjectStage: FC<SelectProjectStageProps> = ({
    onBack,
    onForward,
    setSampleAppIdentifier,
    sampleApps,
    paramsFormId,
    inputFormId
}) => {
    const dispatch = useDispatch()

    const handleBlankProjectSelect = useCallback(() => {
        setSampleAppIdentifier()
        onForward()
    }, [onForward, setSampleAppIdentifier])

    const handleSampleAppSelect = useCallback(
        (sampleAppIdentifier: string) => {
            setSampleAppIdentifier(sampleAppIdentifier)
            onForward()
        },
        [onForward, setSampleAppIdentifier]
    )

    // clear the input on this stage as the user can select a blank project or from sample here
    useEffect(() => {
        dispatch(clearInputsForId(paramsFormId))
        dispatch(clearInputsForId(inputFormId))
    }, [dispatch, inputFormId, paramsFormId])

    return (
        <OnboardingScreenContainer celebrate={false} onForward={onForward}>
            <OnboardingProgress
                currentStage={TemplateInstantiateStage.selectProject}
                back={false}
                onBack={onBack}
            />
            <div
                id="maincontent"
                className="flex flex-1 flex-col w-full justify-center items-center"
                data-testid="onboarding-select-project"
            >
                <Title
                    color="darkviolet"
                    variant="heading1"
                    className="text-center mb-10"
                    weight="semibold"
                >
                    Start with sample data and settings?
                </Title>

                <div className="w-1/2 mt-7 flex flex-col items-start mb-32">
                    <Text
                        component="span"
                        color="darkviolet"
                        className="text-center mb-3"
                        weight="semibold"
                    >
                        No, I’ll set things up myself
                    </Text>
                    <SelectProjectButton
                        key="blank-project"
                        onClick={handleBlankProjectSelect}
                        testId="blank-project"
                        icon={BlankProjectIcon}
                        title="Blank project"
                    />

                    {
                        <Text
                            component="span"
                            color="darkviolet"
                            className="text-center mb-3 mt-2"
                            weight="semibold"
                        >
                            Yes, start with an example
                        </Text>
                    }
                    {sampleApps.map(sampleApp => (
                        <SelectProjectButton
                            key={sampleApp.identifier}
                            title={sampleApp.title}
                            description={sampleApp.description}
                            onClick={() =>
                                handleSampleAppSelect(sampleApp.identifier)
                            }
                            icon={SampleProjectIcon}
                        />
                    ))}
                </div>
            </div>
        </OnboardingScreenContainer>
    )
}

interface SelectProjectButtonProps {
    title: string
    description?: string
    icon: FC<{ className?: string }>
    testId?: string
    onClick: () => void
}
const SelectProjectButton: FC<SelectProjectButtonProps> = ({
    testId,
    onClick,
    icon: Icon,
    title,
    description
}) => (
    <Button
        className="h-20 w-full rounded-lg bg-white flex justify-start items-center p-6 mb-4"
        active={true}
        color="white"
        key="heroes"
        onClick={onClick}
        testId={testId}
    >
        <Icon className="text-rhino-450 w-7 h-8 mr-5.5" />
        <div className="flex flex-col items-start">
            <Text component="span" className="text-base text-grape-900">
                {title}
            </Text>
            <Text component="span" className="text-xs" color="gray">
                {description}
            </Text>
        </div>
    </Button>
)
